import {
  Bot,
  ChartColumn,
  Contact,
  Eye,
  FileText,
  Flag,
  Globe,
  Hammer,
  House,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  type LucideIcon,
  Megaphone,
  PanelsTopLeft,
  Settings,
  Shield,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';

import { GUARDS } from '~/app/access';
import { routes } from '~/app/routes';
import type { Access } from '~/modules/access';

export interface LeafEntry {
  label: string;
  path: string;
}

/** A link, or a collapsible group of links (one level only). */
export interface MenuEntry {
  label: string;
  icon: LucideIcon;
  path?: string;
  subItems?: LeafEntry[];
}

/** A block of the sidebar under its heading ("Tools", "MLS", "AI", "Feature flags"); Dashboard's has none. */
export interface MenuSection {
  label?: string;
  items: MenuEntry[];
}

const DASHBOARD: MenuEntry = { label: 'Dashboard', icon: House, path: routes.dashboard };

const TOOLS: MenuEntry[] = [
  { label: 'Services', icon: Globe, path: routes.services },
  { label: 'Listing videos', icon: Video, path: routes.listingVideos },
  { label: 'Customers', icon: Users, path: routes.customers },
  { label: 'Utilities', icon: Hammer, path: routes.utilities },
];

const MLS: MenuEntry[] = [
  { label: 'Agreements', icon: FileText, path: routes.mlsAgreements },
  { label: 'Feeds', icon: LayoutGrid, path: routes.feeds },
];

const AI: MenuEntry[] = [
  { label: 'Overview', icon: LayoutDashboard, path: routes.ai },
  { label: 'Analytics', icon: ChartColumn, path: routes.aiAnalytics },
  { label: 'Placements', icon: PanelsTopLeft, path: routes.aiPlacements },
  { label: 'Personas', icon: Contact, path: routes.aiPersonas },
  { label: 'Digital assistants', icon: Bot, path: routes.aiDigitalAssistants },
  {
    label: 'Settings',
    icon: Settings,
    subItems: [
      { label: 'General', path: routes.aiSettings('general') },
      { label: 'AI Write', path: routes.aiSettings('ai-write') },
      { label: 'Models', path: routes.aiSettings('models') },
      { label: 'Other', path: routes.aiSettings('other') },
    ],
  },
];

const FEATURE_FLAGS: MenuEntry[] = [
  { label: 'Overview', icon: LayoutDashboard, path: routes.featureFlags },
  { label: 'Flags', icon: Flag, path: routes.featureFlagsSection('flags') },
  { label: 'Product updates', icon: Megaphone, path: routes.featureFlagsSection('updates') },
  { label: 'Feature requests', icon: Lightbulb, path: routes.featureFlagsSection('requests') },
  { label: 'Known users', icon: UserCheck, path: routes.featureFlagsSection('users') },
  { label: 'Preview', icon: Eye, path: routes.featureFlagsSection('preview') },
  {
    label: 'Admin',
    icon: Shield,
    subItems: [
      { label: 'Products', path: routes.featureFlagsAdmin('products') },
      { label: 'Environments', path: routes.featureFlagsAdmin('environments') },
      { label: 'Attributes', path: routes.featureFlagsAdmin('attributes') },
      { label: 'SDK keys', path: routes.featureFlagsAdmin('sdk-keys') },
    ],
  },
];

/** The sidebar for one person: entries behind a guard appear only when its `allow` passes. */
export const buildMenu = (access: Access): MenuSection[] => [
  { items: [DASHBOARD] },
  { label: 'Tools', items: TOOLS },
  {
    label: 'MLS',
    items: MLS.filter(
      (entry) => entry.label !== 'Agreements' || GUARDS.mlsAgreements.allow(access),
    ),
  },
  ...(GUARDS.ai.allow(access) ? [{ label: 'AI', items: AI }] : []),
  ...(GUARDS.featureFlags.allow(access) ? [{ label: 'Feature flags', items: FEATURE_FLAGS }] : []),
];
